import React, { useState, useCallback } from 'react';
import logger from '../../utils/logger';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import { Role, VehicleType } from '../../types';
import {
  TagIcon,
  PlusCircleIcon,
  TrashIcon,
  ListBulletIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { SkeletonCard, SkeletonTable } from '../../components/ui/LoadingSpinner';
import { Button, PageTitle, DeleteConfirmModal } from '../../components/ui';
import { VehicleTypeDetail } from '../../components/vehicle/VehicleTypeDetail';

export const VehicleTypeListPage: React.FC = () => {
  const { user } = useAuth();
  const {
    vehicleTypes,
    vehicleModels,
    landTransporters,
    addVehicleType,
    deleteVehicleType,
    loading,
  } = useMasterData();

  // React Hooksを最初に定義
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingType, setEditingType] = useState<{ id: string; name: string } | null>(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState<VehicleType | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    typeId: string;
    typeName: string;
  } | null>(null);
  // 2層アーキテクチャ - PartnerListPageパターンに統一
  const initialFormState = {
    typeName: '',
  };
  const [newTypeData, setNewTypeData] = useState(initialFormState);

  // 標準的なReact入力ハンドラー
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setNewTypeData(prev => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newTypeData.typeName.trim()) {
        alert('車種名を入力してください。');
        return;
      }

      // 重複チェック
      if (
        vehicleTypes.some(type => type.name.toLowerCase() === newTypeData.typeName.toLowerCase())
      ) {
        alert('同じ名前の車種が既に存在します。');
        return;
      }

      try {
        if (editingType) {
          // 編集モード（将来の拡張用）
          logger.info('車種編集機能は未実装です');
          alert('車種編集機能は未実装です。');
          return;
        } else {
          // 新規追加モード
          await addVehicleType({
            name: newTypeData.typeName.trim(),
          });
        }

        // フォームリセット
        setIsFormVisible(false);
        setEditingType(null);
        setNewTypeData(initialFormState);
      } catch (error) {
        logger.error('車種の保存に失敗しました:', error);
        alert('保存に失敗しました。もう一度お試しください。');
      }
    },
    [newTypeData, editingType, vehicleTypes, addVehicleType, initialFormState]
  );

  // 編集モードをキャンセル
  const cancelEditing = useCallback(() => {
    setEditingType(null);
    setNewTypeData(initialFormState);
    setIsFormVisible(false);
  }, [initialFormState]);

  // 詳細表示ハンドラー
  const handleVehicleTypeClick = (vehicleType: VehicleType) => {
    setSelectedVehicleType(vehicleType);
  };

  const handleCloseDetail = () => {
    setSelectedVehicleType(null);
  };

  // SUPER_ADMIN権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center'>
          <div className='mb-4'>
            <ExclamationTriangleIcon className='h-16 w-16 text-red-500 mx-auto' />
          </div>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white mb-4'>
            アクセス権限がありません
          </h2>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            車種管理機能は、ラクハイ管理者（SUPER_ADMIN）のみアクセス可能です。
          </p>
          <p className='text-sm text-gray-500 dark:text-gray-500 mb-4'>
            車種の閲覧は、車両モデル登録時の選択リストで引き続きご利用いただけます。
          </p>
          <button
            onClick={() => window.history.back()}
            className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
          >
            戻る
          </button>
        </div>
      </div>
    );
  }

  const handleDelete = (typeId: string, typeName: string) => {
    // 使用中チェック
    const usedInModels = vehicleModels.filter(model => model.vehicleTypeId === typeId);
    const usedInTransporters = landTransporters.filter(
      transporter =>
        transporter.drivableVehicleTypes && transporter.drivableVehicleTypes.includes(typeId)
    );

    if (usedInModels.length > 0 || usedInTransporters.length > 0) {
      const usageDetails = [];
      if (usedInModels.length > 0) {
        usageDetails.push(`車両モデル: ${usedInModels.length}件`);
      }
      if (usedInTransporters.length > 0) {
        usageDetails.push(`陸送員: ${usedInTransporters.length}件`);
      }

      alert(`車種「${typeName}」は以下で使用中のため削除できません:\n${usageDetails.join('\n')}`);
      return;
    }

    // 削除確認モーダルを表示
    setDeleteConfirm({ show: true, typeId, typeName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteVehicleType(deleteConfirm.typeId);
      logger.info('✅ 車種削除成功:', deleteConfirm.typeName);
      setDeleteConfirm(null);
      setSelectedVehicleType(null);
    } catch (error) {
      logger.error('❌ 車種削除失敗:', error);
      alert('車種の削除に失敗しました。もう一度お試しください。');
    }
  };

  const getUsageCount = (typeId: string) => {
    const modelCount = vehicleModels.filter(model => model.vehicleTypeId === typeId).length;
    const transporterCount = landTransporters.filter(
      transporter =>
        transporter.drivableVehicleTypes && transporter.drivableVehicleTypes.includes(typeId)
    ).length;
    return { modelCount, transporterCount };
  };

  const TypeList = () => (
    <div>
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0'>
        <h2 className='text-lg sm:text-xl font-semibold text-gray-700 dark:text-white flex items-center'>
          <ListBulletIcon className='h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3' />
          車種一覧 ({vehicleTypes.length})
        </h2>
        <Button
          onClick={() => {
            setEditingType(null);
            setNewTypeData(initialFormState);
            setIsFormVisible(true);
          }}
          variant='primary'
          size='md'
          className='w-full sm:w-auto'
        >
          <PlusCircleIcon className='h-5 w-5 mr-2' />
          <span className='hidden sm:inline'>新規車種を追加</span>
          <span className='sm:hidden'>新規追加</span>
        </Button>
      </div>

      {/* デスクトップ表示 */}
      <div className='hidden lg:block'>
        {loading ? (
          <SkeletonTable rows={5} cols={3} />
        ) : (
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden'>
            <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
              <thead className='bg-gray-50 dark:bg-gray-700'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    車種名
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    使用状況
                  </th>
                  <th className='px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    詳細
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {vehicleTypes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className='px-6 py-8 text-center text-gray-500 dark:text-gray-400'
                    >
                      車種が登録されていません
                    </td>
                  </tr>
                ) : (
                  vehicleTypes.map(type => {
                    const { modelCount, transporterCount } = getUsageCount(type.id);
                    const isInUse = modelCount > 0 || transporterCount > 0;

                    return (
                      <tr
                        key={type.id}
                        onClick={() => handleVehicleTypeClick(type)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleVehicleTypeClick(type);
                          }
                        }}
                        tabIndex={0}
                        role='button'
                        aria-label={`車種「${type.name}」の詳細を表示`}
                        className='hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset'
                      >
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex items-center'>
                            <TagIcon className='h-5 w-5 text-gray-400 mr-2' />
                            <div className='text-sm font-medium text-gray-900 dark:text-white'>
                              {type.name}
                            </div>
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex flex-wrap gap-2'>
                            {modelCount > 0 && (
                              <span className='px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'>
                                車両モデル: {modelCount}
                              </span>
                            )}
                            {transporterCount > 0 && (
                              <span className='px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'>
                                陸送員: {transporterCount}
                              </span>
                            )}
                            {!isInUse && (
                              <span className='px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'>
                                未使用
                              </span>
                            )}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-center'>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleVehicleTypeClick(type);
                            }}
                            className='inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50'
                            title='詳細を表示'
                          >
                            <EyeIcon className='h-5 w-5' />
                          </button>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                          <div className='flex justify-end'>
                            <button
                              onClick={() => handleDelete(type.id, type.name)}
                              disabled={isInUse}
                              className={`p-2 rounded smooth-transition min-h-[44px] min-w-[44px] flex items-center justify-center touch-target button-touch focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                isInUse
                                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed focus:ring-gray-500'
                                  : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 focus:ring-red-500 button-ripple'
                              }`}
                              title={isInUse ? '使用中のため削除できません' : '削除'}
                              aria-label={isInUse ? '使用中のため削除できません' : '車種を削除'}
                            >
                              {isInUse ? (
                                <ExclamationTriangleIcon className='h-4 w-4' />
                              ) : (
                                <TrashIcon className='h-4 w-4' />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* モバイル・タブレット表示 */}
      <div className='lg:hidden'>
        {loading ? (
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : vehicleTypes.length === 0 ? (
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center'>
            <div className='text-gray-500 dark:text-gray-400'>車種が登録されていません</div>
          </div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
            {vehicleTypes.map(type => {
              const { modelCount, transporterCount } = getUsageCount(type.id);
              const isInUse = modelCount > 0 || transporterCount > 0;

              return (
                <div
                  key={type.id}
                  className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-5 hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-200 dark:hover:border-blue-800'
                >
                  <div className='flex justify-between items-start mb-3'>
                    <div className='flex items-center flex-1 min-w-0'>
                      <TagIcon className='h-5 w-5 text-gray-400 mr-2 flex-shrink-0' />
                      <div className='flex-1 min-w-0'>
                        <h3 className='text-base sm:text-lg font-bold text-gray-800 dark:text-white truncate'>
                          {type.name}
                        </h3>
                        <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1'>
                          ID: {type.id}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(type.id, type.name)}
                      disabled={isInUse}
                      className={`p-2 rounded smooth-transition min-h-[44px] min-w-[44px] flex items-center justify-center touch-target button-touch focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        isInUse
                          ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed focus:ring-gray-500'
                          : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 focus:ring-red-500 button-ripple'
                      }`}
                      title={isInUse ? '使用中のため削除できません' : '削除'}
                      aria-label={isInUse ? '使用中のため削除できません' : '車種を削除'}
                    >
                      {isInUse ? (
                        <ExclamationTriangleIcon className='h-4 w-4' />
                      ) : (
                        <TrashIcon className='h-4 w-4' />
                      )}
                    </button>
                  </div>
                  <div className='space-y-2 mb-3'>
                    <div className='flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                      <span className='font-medium w-16 sm:w-20 flex-shrink-0'>使用状況:</span>
                      <div className='flex flex-wrap gap-1'>
                        {modelCount > 0 && (
                          <span className='px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'>
                            車両モデル: {modelCount}
                          </span>
                        )}
                        {transporterCount > 0 && (
                          <span className='px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'>
                            陸送員: {transporterCount}
                          </span>
                        )}
                        {!isInUse && (
                          <span className='px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'>
                            未使用
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700'>
                    <button
                      onClick={() => handleVehicleTypeClick(type)}
                      className='inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50'
                    >
                      <EyeIcon className='h-4 w-4' />
                      詳細を見る
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {vehicleTypes.length > 0 && (
        <div className='mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4'>
          <div className='flex items-start'>
            <ExclamationTriangleIcon className='h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5' />
            <div>
              <h3 className='text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1'>
                削除制限について
              </h3>
              <p className='text-sm text-yellow-700 dark:text-yellow-300'>
                車両モデルや陸送員で使用中の車種は削除できません。
                削除するには、まず使用している車両モデルや陸送員から該当の車種を削除してください。
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageTitle icon={TagIcon} title='車種管理' />

      {isFormVisible ? (
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6'>
          <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
            {editingType ? '車種編集' : '新規車種追加'}
          </h2>
          <form onSubmit={handleSubmit} className='space-y-4'>
            <div>
              <label
                htmlFor='typeName'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
              >
                車種名 <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                id='typeName'
                name='typeName'
                value={newTypeData.typeName}
                onChange={handleInputChange}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white'
                placeholder='車種名を入力してください'
                required
              />
            </div>
            <div className='flex justify-end space-x-3'>
              <Button type='button' onClick={cancelEditing} variant='secondary' size='md'>
                キャンセル
              </Button>
              <Button type='submit' variant='primary' size='md'>
                {editingType ? '更新' : '追加'}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <TypeList />
      )}

      {/* 車種詳細モーダル */}
      {selectedVehicleType && (
        <VehicleTypeDetail
          vehicleType={selectedVehicleType}
          onClose={handleCloseDetail}
          onDelete={handleDelete}
          isModal={true}
        />
      )}

      {/* 削除確認モーダル */}
      <DeleteConfirmModal
        isOpen={deleteConfirm?.show || false}
        itemName={deleteConfirm?.typeName || ''}
        itemType='車種'
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};
